<template>
  <o-dialog
    ref="dialog"
    :title="$t('order.addCustomerCourses')"
    :info="$t('components.order.actions.addCustomerCourses.info')"
    :options="dialogOptions"
    :loading="loading"
    :portal="portal"
    :valid="validate"
  >
    <div class="pt-4 text-left">
      <o-form ref="form">
        <o-field-group
          v-slot="{ status }"
          :label="$t('general.customer')"
          :name="$t('general.customer')"
          :info="$t('components.order.actions.addCustomerCourses.customerInfo')"
          class="mb-4 block"
          rules="required"
        >
          <customer-select v-model="customer" :status="status" @change="fetchCourses" />
        </o-field-group>

        <o-field-group
          v-if="customer"
          v-slot="{ status }"
          :label="$t('general.course')"
          :name="$t('general.course')"
          class="block"
          rules="required"
        >
          <div class="block -mx-4 border-t border-b bg-gray-100 overflow-hidden dark:bg-gray-900">
            <div v-if="!hasCourses && !loadingCourses" class="flex flex-col items-center justify-center mt-6 mb-6">
              <p class="text-sm text-gray-700 dark:text-gray-200">
                {{ $t('components.order.actions.addCustomerCourses.noUnpaid', { name: customer?.displayName }) }}
              </p>
            </div>

            <div v-if="loadingCourses" class="flex items-center justify-center gap-2 mt-6 mb-6" role="status">
              <o-loader size="20px" />
              <p class="text-sm text-gray-700 dark:text-gray-200">
                {{ $t('components.order.actions.addCustomerCourses.loadingUnpaid', { name: customer?.displayName }) }}
              </p>
            </div>

            <checkbox-group-field
              v-if="hasCourses && !loadingCourses"
              v-model="selected"
              :options="courseOptions"
              :status="status"
              search
              class="h-full"
            >
              <template #option="{ option }">
                <div class="w-full ml-3 text-gray-800 pt-1 leading-none dark:text-gray-100">
                  <p class="mb-1">
                    {{ option.value.course.name }}
                  </p>
                  <p class="text-gray-600 dark:text-gray-300">
                    {{ option.value.courseRun.information }}
                  </p>
                </div>
              </template>
            </checkbox-group-field>
          </div>
        </o-field-group>
      </o-form>
    </div>
  </o-dialog>
</template>

<script>
import CustomerSelect from '@/components/customer/elements/Select.vue'

import {
  CheckboxGroupField,
} from '@/components/fields'

export default {
  components: {
    CustomerSelect,
    CheckboxGroupField,
  },
  props: {
    order: {
      type: Object,
      required: true,
    },
    portal: {
      type: String,
      default: undefined,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      selected: [],
      courses: null,
      customer: null,
      loadingCourses: true,
      latestCoursesRequest: 0,
    }
  },
  computed: {
    dialogOptions () {
      if (!this.loading) {
        const options = [
          { text: this.$t('general.cancel'), type: 'cancel' },
        ]

        if (this.canEnrol) {
          options.push({
            text: this.$t('pages.course.view.enrolCustomer'),
            type: 'confirm',
            variant: 'white',
            validate: false,
            flat: true,
            outline: true,
            callback: this.handleCreateCustomerCourse,
          })
        }

        if (this.hasCourses) {
          options.push({
            text: this.$t('components.order.actions.addCustomerCourses.addSelected'),
            type: 'confirm',
            callback: this.confirm,
            disabled: !this.selected.length,
          })
        }

        return options
      }

      return []
    },
    courseOptions () {
      const courses = this.courses

      if (!courses) { return [] }

      return courses
        .map(course => ({
          value: course,
          text: course.course.name,
          info: course.courseRun.information,
        }))
    },
    hasCourses () {
      return this.courses && !!this.courseOptions.length
    },
    canEnrol () {
      return this.$gates.get('courses') && this.$grants.all({ customerCourse: ['create'] })
    },
  },
  methods: {
    open () {
      this.customer = this.order.customer
      this.selected = []
      this.courses = null
      this.fetchCourses()

      this.$refs.dialog.open()
    },
    async confirm () {
      this.$emit('update:loading', true)

      try {
        const selected = this.selected
        const order = (await this.$api.order.addCustomerCourses(
          this.order.objectID(),
          selected.map(({ idCustomerCourse }) => String(idCustomerCourse)),
        )).data

        this.$emit('update:order', order)

        this.$notification.toast({
          title: this.$t('components.order.actions.addCustomerCourses.success'),
          text: this.$t('components.order.actions.addCustomerCourses.successInfo'),
          variant: 'success',
        })

        this.$emit('update:loading', false)

        return true
      } catch (e) {
        console.log(e)
      }

      this.$emit('update:loading', false)
    },
    validate () {
      return this.$refs.form.validate()
    },
    handleCreateCustomerCourse () {
      this.$emit('create')
      this.$refs.dialog.close()
    },
    async fetchCourses () {
      const requestId = ++this.latestCoursesRequest
      this.selected = []
      this.courses = null

      const { customer } = this

      if (customer) {
        this.loadingCourses = true

        try {
          const response = await this.$api.customer.courses(customer.objectID(), { unpaid: true })

          if (requestId !== this.latestCoursesRequest) return

          const items = response.items

          // Safety net: exclude any course already attached to an order or paid,
          // in case the backend unpaid filter is unavailable.
          this.courses = items
            .filter(course => !course.order && !course.isPaid)
        } catch (e) {
          if (requestId !== this.latestCoursesRequest) return

          console.error(e)
          this.courses = []
        } finally {
          if (requestId === this.latestCoursesRequest) {
            this.loadingCourses = false
          }
        }
      } else {
        this.courses = []
        this.loadingCourses = false
      }
    },
  },
}
</script>
